 import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { UserOptions } from 'jspdf-autotable';
import { DatePipe } from '@angular/common';
import { CollisService } from '../collis/collis.service';
import { BagageService } from '../bagage/bagage.service';
import { RecetteService } from '../recette/recette.service';
import { PassagerService } from '../passager/passager.service';
import { ImageCacheService } from '../image/image-cache.service';
import { VoyageService } from '../voyage/voyage.service';

interface jsPDFWithPlugin extends jsPDF {
  autoTable: (options: UserOptions) => jsPDF;
  lastAutoTable: { finalY: number };
}

@Injectable({ providedIn: 'root' })
export class SessionPrintService {
  private base64: any;

  constructor(
    private collisservice: CollisService,
    private bagageservice: BagageService,
    private recetteservice: RecetteService,
    private passagerservice: PassagerService,
    private datepipe: DatePipe,
    private imageCacheService: ImageCacheService,
    private voyageservice: VoyageService
  ) {
    this.imageCacheService
      .getImage('../../../../assets/images/logogen.jpg')
      .subscribe({
        next: (base64: string) => {
          this.base64 = base64;
        },
        error: (error: any) => {
          console.error('Error loading logo:', error);
        }
      });
  }

  private addAmt(arr: any[], type: string, totals: { cash: number, mobile: number, orange: number }) {
    if (!arr) return;
    arr.forEach((item: any) => {
      let pm = 'CASH';
      if (type === 'passager') pm = item.passager?.paymentMethod || item.paymentMethod || 'CASH';
      else pm = item.paymentMethod || 'CASH';
      
      let amt = 0;
      if (item.montant !== undefined && item.montant !== null) {
        amt = Number(item.montant);
      } else if (item.tarif !== undefined && item.tarif !== null) {
        amt = Number(item.tarif);
      } else if (item.voyage?.tarif?.tarif !== undefined && item.voyage?.tarif?.tarif !== null) {
        amt = Number(item.voyage.tarif.tarif);
      }

      if (pm === 'MOBILE' || pm === 'MOBILE_MONEY' || pm === 'MOBILE MONEY') totals.mobile += amt;
      else if (pm === 'ORANGE' || pm === 'ORANGE_MONEY' || pm === 'ORANGE MONEY') totals.orange += amt;
      else totals.cash += amt;
    });
  }

  private addHeaderInfo(doc: any, datas: any, title: string, cashTotal: number, mobileTotal: number, orangeTotal: number = 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERAL EXPRESS VOYAGES', 4.25, 0.4, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(title + ' #' + datas.idsessiion + '/' + datas.sessionCount, 4.25, 0.6, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    // Left column
    doc.text('Agent:  ' + datas.name, 0.5, 0.9);
    doc.text('S/N:  ' + datas.idsessiion, 0.5, 1.1);
    doc.text('Montant Total:  ' + datas.sessionAmount + ' FCFA', 0.5, 1.3);
    doc.text('Espèces (CASH):  ' + cashTotal + ' FCFA', 0.5, 1.5);
    doc.text('Mobile Money:  ' + mobileTotal + ' FCFA', 0.5, 1.7);
    doc.text('Orange Money:  ' + orangeTotal + ' FCFA', 0.5, 1.9);
    
    // Right column
    doc.text('Date Ouverture S/N:  ' + this.datepipe.transform(datas.creationDate, 'yyyy-MM-dd HH:mm:ss'), 4.5, 0.9);
    doc.text('Date Fermeture S/N:  ' + this.datepipe.transform(datas.closingDate, 'yyyy-MM-dd HH:mm:ss'), 4.5, 1.1);
    doc.text('Date Impression:  ' + this.datepipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss'), 4.5, 1.3);

    if (this.base64) {
      doc.addImage(this.base64, 'JPEG', 6.8, 0.2, 1.2, 1.2);
    }
  }

  async printColisSession(datas: any): Promise<any> {
    return this.printMixedSessionSummary(datas);
  }

  async printBagageSession(datas: any): Promise<any> {
    return this.printMixedSessionSummary(datas);
  }

  async printPassagerSession(datas: any): Promise<any> {
    return this.printGuichetSummary(datas);
  }

  async printGuichetSummary(datas: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const page = 0; const size = 5000;
      this.passagerservice.getallticketinSession(datas.idsessiion, page, size).subscribe((passager: any) => {
        const data = passager.content.filter((item: any) => item.statutpassager !== 'cancel');
        this.recetteservice.getallrecetteinSession(datas.idsessiion, page, size).subscribe((recette: any) => {
          const recetteData = recette.content;
          this.recetteservice.getalldepenseinSession(datas.idsessiion, page, size).subscribe((depense: any) => {
            const depenseData = depense.content;
            this.voyageservice.getSessionSummaryByMontant(datas.idsessiion).subscribe((montantSummary: any) => {

            const montantSummaryRows = (montantSummary || [])
              .map((row: any) => ({ montant: row[0], total: row[1], ticketCount: row[2] }))
              .sort((a: any, b: any) => a.montant - b.montant);
            const montantOverallTickets = montantSummaryRows.reduce((sum: number, row: any) => sum + row.ticketCount, 0);
            const montantOverallTotal = montantSummaryRows.reduce((sum: number, row: any) => sum + row.total, 0);

            const printerType = localStorage.getItem('guichet_printer_type') || 'THERMAL';
            let doc: any;
            
            let totals = { cash: 0, mobile: 0, orange: 0 };
            let passagerTotals = { cash: 0, mobile: 0, orange: 0 };
            let recetteTotals = { cash: 0, mobile: 0, orange: 0 };
            
            this.addAmt(data, 'passager', passagerTotals);
            this.addAmt(recetteData, 'recette', recetteTotals);
            
            totals.cash += passagerTotals.cash + recetteTotals.cash;
            totals.mobile += passagerTotals.mobile + recetteTotals.mobile;
            totals.orange += passagerTotals.orange + recetteTotals.orange;
            
            let depenseTotal = 0;
            if (depenseData) {
              depenseData.forEach((item: any) => { 
                const amt = Number(item.tarif || 0);
                totals.cash -= amt; 
                depenseTotal += amt;
              });
            }

            if (printerType === 'POS' || printerType === 'THERMAL') {
              const docWidth = 80;
              const docHeight = 150;
              doc = new jsPDF('p', 'mm', [docWidth, docHeight], true) as jsPDFWithPlugin;
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              doc.text("RAPPORT DE FIN DE SESSION", docWidth / 2, 10, { align: 'center' });
              
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.text(`ID: ${datas.idsessiion}`, docWidth / 2, 15, { align: 'center' });
              doc.text(`Employé: ${datas.name || ''}`, docWidth / 2, 20, { align: 'center' });
              doc.text(`Ouverture: ${this.datepipe.transform(datas.creationDate, 'dd/MM/yyyy HH:mm')}`, docWidth / 2, 25, { align: 'center' });
              doc.text(`Fermeture: ${this.datepipe.transform(datas.closingDate, 'dd/MM/yyyy HH:mm')}`, docWidth / 2, 30, { align: 'center' });
              
              doc.line(5, 33, docWidth - 5, 33);
              
              let y = 38;
              doc.setFontSize(9);
              
              const pTot = passagerTotals.cash + passagerTotals.mobile + passagerTotals.orange;
              if (pTot > 0) {
                doc.setFont('helvetica', 'bold');
                doc.text("PASSAGERS", 5, y); y += 5;
                doc.setFont('helvetica', 'normal');
                doc.text(`Total: ${pTot} FCFA`, 5, y); y += 5;
                doc.text(`Cash: ${passagerTotals.cash} FCFA`, 5, y); y += 5;
                doc.text(`Mobile: ${passagerTotals.mobile} FCFA`, 5, y); y += 5;
                doc.text(`Orange: ${passagerTotals.orange} FCFA`, 5, y); y += 8;
              }
              
              const rTot = recetteTotals.cash + recetteTotals.mobile + recetteTotals.orange;
              if (rTot > 0) {
                doc.setFont('helvetica', 'bold');
                doc.text("AUTRES RECETTES", 5, y); y += 5;
                doc.setFont('helvetica', 'normal');
                doc.text(`Total: ${rTot} FCFA`, 5, y); y += 5;
                doc.text(`Cash: ${recetteTotals.cash} FCFA`, 5, y); y += 5;
                doc.text(`Mobile: ${recetteTotals.mobile} FCFA`, 5, y); y += 5;
                doc.text(`Orange: ${recetteTotals.orange} FCFA`, 5, y); y += 8;
              }
              
              if (depenseTotal > 0) {
                doc.setFont('helvetica', 'bold');
                doc.text("AUTRES DEPENSES", 5, y); y += 5;
                doc.setFont('helvetica', 'normal');
                doc.text(`Total: ${depenseTotal} FCFA`, 5, y); y += 10;
              }
              
              doc.line(5, y, docWidth - 5, y); y += 5;
              
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              doc.text("TOTAL GENERAL", docWidth / 2, y, { align: 'center' }); y += 6;
              doc.setFont('helvetica', 'normal');
              doc.text(`Cash Total: ${totals.cash} FCFA`, docWidth / 2, y, { align: 'center' }); y += 5;
              doc.text(`Mobile Money: ${totals.mobile} FCFA`, docWidth / 2, y, { align: 'center' }); y += 5;
              doc.text(`Orange Money: ${totals.orange} FCFA`, docWidth / 2, y, { align: 'center' }); y += 8;

              if (montantSummaryRows.length > 0) {
                doc.line(5, y, docWidth - 5, y); y += 5;
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.text("RESUME PAR MONTANT", docWidth / 2, y, { align: 'center' }); y += 6;
                doc.setFont('helvetica', 'normal');
                montantSummaryRows.forEach((row: any) => {
                  doc.text(`${row.montant} FCFA x ${row.ticketCount} = ${row.total} FCFA`, docWidth / 2, y, { align: 'center' });
                  y += 5;
                });
                doc.setFont('helvetica', 'bold');
                doc.text(`TOTAL: ${montantOverallTickets} billets - ${montantOverallTotal} FCFA`, docWidth / 2, y, { align: 'center' });
              }
            } else {
              doc = new jsPDF('p', 'in', [8.27, 11.69]) as jsPDFWithPlugin;
              this.addHeaderInfo(doc, datas, 'RAPPORT DE FIN DE SESSION', totals.cash, totals.mobile, totals.orange);
              
              let y = 2.5;
              const pTot = passagerTotals.cash + passagerTotals.mobile + passagerTotals.orange;
              const rTot = recetteTotals.cash + recetteTotals.mobile + recetteTotals.orange;
              const netTotal = totals.cash + totals.mobile + totals.orange;

              autoTable(doc, {
                head: [['N° Session', 'Agent', 'Passagers', 'Autres Recettes', 'Dépenses', 'Solde Net']],
                body: [[
                  datas.idsessiion || 'N/A',
                  datas.name || 'N/A',
                  `${pTot.toLocaleString()} F`,
                  `${rTot.toLocaleString()} F`,
                  `${depenseTotal.toLocaleString()} F`,
                  `${netTotal.toLocaleString()} F`
                ]],
                startY: y,
                theme: 'striped',
                headStyles: { fillColor: '#1a237e', textColor: '#ffffff', fontStyle: 'bold' },
                styles: { fontSize: 10, font: 'helvetica', lineColor: '#cccccc', lineWidth: 0.01 },
                columnStyles: {
                  0: { cellWidth: 1.2 },
                  1: { cellWidth: 1.8 },
                  2: { cellWidth: 1.1, halign: 'right' },
                  3: { cellWidth: 1.1, halign: 'right' },
                  4: { cellWidth: 1.1, halign: 'right' },
                  5: { cellWidth: 1.2, halign: 'right' }
                }
              });
              y = doc.lastAutoTable.finalY + 0.4;

              if (montantSummaryRows.length > 0) {
                y += 0.2;
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text("RESUME PAR MONTANT", 4.135, y, { align: 'center' });
                y += 0.4;
                doc.setFontSize(12);
                montantSummaryRows.forEach((row: any) => {
                  doc.setFont('helvetica', 'normal');
                  doc.text(`${row.montant} FCFA x ${row.ticketCount} billets = ${row.total} FCFA`, 1.2, y);
                  y += 0.3;
                });
                doc.setFont('helvetica', 'bold');
                doc.text(`TOTAL: ${montantOverallTickets} billets - ${montantOverallTotal} FCFA`, 1.0, y);
                y += 0.3;
              }
            }

            window.open(doc.output('bloburl').toString(), '_blank');
            resolve(true);
            }, (error: any) => reject(error));
          }, (error: any) => reject(error));
        }, (error: any) => reject(error));
      }, (error: any) => reject(error));
    });
  }

  async printMixedSessionSummary(datas: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const page = 0; const size = 5000;
      this.collisservice.getallcollisinSession(datas.idsessiion, page, size).subscribe((collis: any) => {
        const data = collis.content.filter((item: any) => item.statutcollis !== 'cancel' && (!item.billnumber || item.billnumber.trim() === ''));
        this.bagageservice.getallbagageinSession(datas.idsessiion, page, size).subscribe((bagage: any) => {
          const bagageData = bagage.content.filter((item: any) => item.statutbagage !== 'cancel' && (!item.billnumber || item.billnumber.trim() === ''));
          this.recetteservice.getallrecetteinSession(datas.idsessiion, page, size).subscribe((recette: any) => {
            const recetteData = recette.content;
            this.recetteservice.getalldepenseinSession(datas.idsessiion, page, size).subscribe((depense: any) => {
              const depenseData = depense.content;
              this.collisservice.getColisSummaryByTarif(datas.idsessiion).subscribe((tarifSummary: any) => {

              const tarifSummaryRows = (tarifSummary || [])
                .map((row: any) => ({ tarif: row[0], total: row[1], parcelCount: row[2] }))
                .sort((a: any, b: any) => a.tarif - b.tarif);
              const tarifOverallParcels = tarifSummaryRows.reduce((sum: number, row: any) => sum + row.parcelCount, 0);
              const tarifOverallTotal = tarifSummaryRows.reduce((sum: number, row: any) => sum + row.total, 0);

              const printerType = localStorage.getItem('parcel_printer_type') || 'MATRIX';
              let doc: any;

              let totals = { cash: 0, mobile: 0, orange: 0 };
              let colisTotals = { cash: 0, mobile: 0, orange: 0 };
              let bagageTotals = { cash: 0, mobile: 0, orange: 0 };
              let recetteTotals = { cash: 0, mobile: 0, orange: 0 };
              
              this.addAmt(data, 'colis', colisTotals);
              this.addAmt(bagageData, 'bagage', bagageTotals);
              this.addAmt(recetteData, 'recette', recetteTotals);
              
              totals.cash += colisTotals.cash + bagageTotals.cash + recetteTotals.cash;
              totals.mobile += colisTotals.mobile + bagageTotals.mobile + recetteTotals.mobile;
              totals.orange += colisTotals.orange + bagageTotals.orange + recetteTotals.orange;
              
              let depenseTotal = 0;
              if (depenseData) {
                depenseData.forEach((item: any) => { 
                  const amt = Number(item.tarif || 0);
                  totals.cash -= amt; 
                  depenseTotal += amt;
                });
              }

              if (printerType === 'POS' || printerType === 'THERMAL') {
                const docWidth = 80;
                const docHeight = 150;
                doc = new jsPDF('p', 'mm', [docWidth, docHeight], true) as jsPDFWithPlugin;
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text("RAPPORT DE FIN DE SESSION", docWidth / 2, 10, { align: 'center' });
                
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`ID: ${datas.idsessiion}`, docWidth / 2, 15, { align: 'center' });
                doc.text(`Employé: ${datas.name || ''}`, docWidth / 2, 20, { align: 'center' });
                doc.text(`Ouverture: ${this.datepipe.transform(datas.creationDate, 'dd/MM/yyyy HH:mm')}`, docWidth / 2, 25, { align: 'center' });
                doc.text(`Fermeture: ${this.datepipe.transform(datas.closingDate, 'dd/MM/yyyy HH:mm')}`, docWidth / 2, 30, { align: 'center' });
                
                doc.line(5, 33, docWidth - 5, 33);
                
                let y = 38;
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                
                const colTot = colisTotals.cash + colisTotals.mobile + colisTotals.orange;
                if (colTot > 0) {
                  doc.setFont('helvetica', 'bold');
                  doc.text("COLIS", 5, y); y += 5;
                  doc.setFont('helvetica', 'normal');
                  doc.text(`Total: ${colTot} FCFA`, 5, y); y += 5;
                  doc.text(`Cash: ${colisTotals.cash} FCFA`, 5, y); y += 5;
                  doc.text(`Mobile: ${colisTotals.mobile} FCFA`, 5, y); y += 5;
                  doc.text(`Orange: ${colisTotals.orange} FCFA`, 5, y); y += 8;
                }
                
                const bTot = bagageTotals.cash + bagageTotals.mobile + bagageTotals.orange;
                if (bTot > 0) {
                  doc.setFont('helvetica', 'bold');
                  doc.text("BAGAGES", 5, y); y += 5;
                  doc.setFont('helvetica', 'normal');
                  doc.text(`Total: ${bTot} FCFA`, 5, y); y += 5;
                  doc.text(`Cash: ${bagageTotals.cash} FCFA`, 5, y); y += 5;
                  doc.text(`Mobile: ${bagageTotals.mobile} FCFA`, 5, y); y += 5;
                  doc.text(`Orange: ${bagageTotals.orange} FCFA`, 5, y); y += 8;
                }
                
                const rTot = recetteTotals.cash + recetteTotals.mobile + recetteTotals.orange;
                if (rTot > 0) {
                  doc.setFont('helvetica', 'bold');
                  doc.text("AUTRES RECETTES", 5, y); y += 5;
                  doc.setFont('helvetica', 'normal');
                  doc.text(`Total: ${rTot} FCFA`, 5, y); y += 5;
                  doc.text(`Cash: ${recetteTotals.cash} FCFA`, 5, y); y += 5;
                  doc.text(`Mobile: ${recetteTotals.mobile} FCFA`, 5, y); y += 5;
                  doc.text(`Orange: ${recetteTotals.orange} FCFA`, 5, y); y += 8;
                }
                
                if (depenseTotal > 0) {
                  doc.setFont('helvetica', 'bold');
                  doc.text("AUTRES DEPENSES", 5, y); y += 5;
                  doc.setFont('helvetica', 'normal');
                  doc.text(`Total: ${depenseTotal} FCFA`, 5, y); y += 10;
                }
                
                doc.line(5, y, docWidth - 5, y); y += 5;
                
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text("TOTAL GENERAL", docWidth / 2, y, { align: 'center' }); y += 6;
                doc.setFont('helvetica', 'normal');
                doc.text(`Cash Total: ${totals.cash} FCFA`, docWidth / 2, y, { align: 'center' }); y += 5;
                doc.text(`Mobile Money: ${totals.mobile} FCFA`, docWidth / 2, y, { align: 'center' }); y += 5;
                doc.text(`Orange Money: ${totals.orange} FCFA`, docWidth / 2, y, { align: 'center' }); y += 8;

                if (tarifSummaryRows.length > 0) {
                  doc.line(5, y, docWidth - 5, y); y += 5;
                  doc.setFontSize(9);
                  doc.setFont('helvetica', 'bold');
                  doc.text("RESUME PAR TARIF", docWidth / 2, y, { align: 'center' }); y += 6;
                  doc.setFont('helvetica', 'normal');
                  tarifSummaryRows.forEach((row: any) => {
                    doc.text(`${row.tarif} FCFA x ${row.parcelCount} = ${row.total} FCFA`, docWidth / 2, y, { align: 'center' });
                    y += 5;
                  });
                  doc.setFont('helvetica', 'bold');
                  doc.text(`TOTAL: ${tarifOverallParcels} colis - ${tarifOverallTotal} FCFA`, docWidth / 2, y, { align: 'center' });
                }
              } else {
                doc = new jsPDF('p', 'in', [8.27, 11.69]) as jsPDFWithPlugin;
                this.addHeaderInfo(doc, datas, 'RAPPORT DE FIN DE SESSION', totals.cash, totals.mobile, totals.orange);
                
                let y = 2.5;
                const colTot = colisTotals.cash + colisTotals.mobile + colisTotals.orange;
                const bTot = bagageTotals.cash + bagageTotals.mobile + bagageTotals.orange;
                const rTot = recetteTotals.cash + recetteTotals.mobile + recetteTotals.orange;
                const netTotal = totals.cash + totals.mobile + totals.orange;

                autoTable(doc, {
                  head: [['N° Session', 'Agent', 'Colis', 'Bagages', 'Autres Recettes', 'Dépenses', 'Solde Net']],
                  body: [[
                    datas.idsessiion || 'N/A',
                    datas.name || 'N/A',
                    `${colTot.toLocaleString()} F`,
                    `${bTot.toLocaleString()} F`,
                    `${rTot.toLocaleString()} F`,
                    `${depenseTotal.toLocaleString()} F`,
                    `${netTotal.toLocaleString()} F`
                  ]],
                  startY: y,
                  theme: 'striped',
                  headStyles: { fillColor: '#1a237e', textColor: '#ffffff', fontStyle: 'bold' },
                  styles: { fontSize: 10, font: 'helvetica', lineColor: '#cccccc', lineWidth: 0.01 },
                  columnStyles: {
                    0: { cellWidth: 1.1 },
                    1: { cellWidth: 1.6 },
                    2: { cellWidth: 1.0, halign: 'right' },
                    3: { cellWidth: 1.0, halign: 'right' },
                    4: { cellWidth: 1.0, halign: 'right' },
                    5: { cellWidth: 1.0, halign: 'right' },
                    6: { cellWidth: 1.1, halign: 'right' }
                  }
                });
                y = doc.lastAutoTable.finalY + 0.4;

                if (tarifSummaryRows.length > 0) {
                  y += 0.2;
                  doc.setFontSize(14);
                  doc.setFont('helvetica', 'bold');
                  doc.text("RESUME PAR TARIF", 4.135, y, { align: 'center' });
                  y += 0.4;
                  doc.setFontSize(12);
                  tarifSummaryRows.forEach((row: any) => {
                    doc.setFont('helvetica', 'normal');
                    doc.text(`${row.tarif} FCFA x ${row.parcelCount} colis = ${row.total} FCFA`, 1.2, y);
                    y += 0.3;
                  });
                  doc.setFont('helvetica', 'bold');
                  doc.text(`TOTAL: ${tarifOverallParcels} colis - ${tarifOverallTotal} FCFA`, 1.0, y);
                  y += 0.3;
                }
              }

              window.open(doc.output('bloburl').toString(), '_blank');
              resolve(true);
              }, (error: any) => reject(error));
            }, (error: any) => reject(error));
          }, (error: any) => reject(error));
        }, (error: any) => reject(error));
      }, (error: any) => reject(error));
    });
  }
}
